import React, { createContext, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { meQuery } from '@witness/graphql';
import ChatManager from './ChatManager';
import { useState } from 'react';
import { serializeConversations } from './chat-helpers';

const ChatContext = createContext(null);

export default ChatContext;

const CONVERSATION_LIMIT = 20;
const MESSAGE_LIMIT = 30;
const SORT_MESSAGES = (a, b) => a.sentAt - b.sentAt;

export const ChatProvider = ({ children }) => {
  const [loadingConversations, setLoadingCoversations] = useState(false);
  const [conversations, setConversations] = useState({});
  const [currentConversation, setCurrentConversation] = useState(null);
  const [currentMessages, setCurrentMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [fetchingMoreMessages, setFetchingMoreMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [fetchingMoreConversations, setFetchingMoreConversations] = useState(false);
  // Optimistic media upload: a local preview shown as a "uploading" bubble until
  // the real message arrives. { url, isImage } | null.
  const [uploadingMedia, setUploadingMedia] = useState(null);

  const { data } = useQuery(meQuery);
  const chatInstance = useMemo(() => ChatManager.getInstance(), []);

  const updateTotalUnread = useCallback(() => {
    chatInstance.fetchAllUnreadMessageCount().then((count) => setTotalUnreadCount(count));
  }, [chatInstance]);

  const handleConversationUpdate = useCallback(
    (conversation) => {
      updateTotalUnread();
      setConversations((current) => {
        return {
          ...current,
          [conversation.groupId]: conversation,
        };
      });
    },
    [updateTotalUnread],
  );

  const toggleCurrentConversation = useCallback((conversation) => {
    setCurrentConversation((current) =>
      current && !conversation.isNew && current.groupId === conversation.groupId
        ? null
        : conversation,
    );

    setHasMoreMessages(true);
  }, []);

  const handleMessageUpdate = useCallback(
    (message) => {
      updateTotalUnread();
      setCurrentMessages((current) => [...current, message]);
    },
    [updateTotalUnread],
  );

  const sendTextMessage = useCallback(
    (text, isGroup) => {
      if (sendingMessage) {
        return Promise.resolve();
      }

      setSendingMessage(true);
      return chatInstance
        .sendTextMessage(
          text,
          currentConversation?.isGroup,
          currentConversation?.isNew ? currentConversation.groupId : undefined,
        )
        .finally(() => setSendingMessage(false));
    },
    [chatInstance, currentConversation, sendingMessage],
  );
  
  const sendMediaMessage = useCallback(
    (file) => {
      if (sendingMessage) {
        return Promise.resolve();
      }

      setSendingMessage(true);

      // Optimistic placeholder: local preview (object URL for images) shown while
      // the file uploads, so the user sees an immediate "uploading" bubble.
      const isImage = (file.type || '').includes('image');
      const previewUrl = isImage ? URL.createObjectURL(file) : null;
      setUploadingMedia({ url: previewUrl, isImage, name: file.name });

      // ChatManager detects image/video/file from the File mime type and uploads.
      return chatInstance
        .sendMediaMessage({ file })
        .finally(() => {
          setSendingMessage(false);
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setUploadingMedia(null);
        });
    },
    [chatInstance, sendingMessage],
  );

  const markAsRead = useCallback(async () => {
    if (currentConversation && currentConversation.unreadMessageCount) {
      await chatInstance.markConversationRead(currentConversation.groupId);
      updateTotalUnread();
    }
  }, [chatInstance, currentConversation, updateTotalUnread]);

  const fetchMoreMessages = useCallback(() => {
    if (hasMoreMessages && !fetchingMoreMessages) {
      setFetchingMoreMessages(true);

      chatInstance
        .fetchMessages()
        .then((moreMessages) => {
          if (moreMessages.length < MESSAGE_LIMIT) {
            setHasMoreMessages(false);
          }

          setCurrentMessages((curr) => [...curr, ...moreMessages].sort(SORT_MESSAGES));
        })
        .finally(() => setFetchingMoreMessages(false));
    }
  }, [chatInstance, hasMoreMessages, fetchingMoreMessages]);

  const logout = useCallback(() => {
    setLoadingCoversations(false);
    setConversations({});
    setCurrentConversation(null);
    setCurrentMessages([]);
    setLoadingMessages(false);
    setSendingMessage(false);
    setHasMoreMessages(true);
    setTotalUnreadCount(0);

    ChatManager.logout();
  }, []);

  useEffect(() => {
    setCurrentMessages([]);
    if (currentConversation) {
      setLoadingMessages(true);
      chatInstance
        .subscribeToGroup({
          groupId: currentConversation.groupId,
          onMessage: handleMessageUpdate,
          messageLimit: MESSAGE_LIMIT,
          isGroup: currentConversation.isGroup,
        })
        .then((messages) => setCurrentMessages(messages))
        .finally(() => setLoadingMessages(false));
    }
    // Stop the open-conversation fast poll when leaving/switching the conversation.
    return () => chatInstance.unsubscribeFromGroup();
  }, [currentConversation, chatInstance, handleMessageUpdate]);

  // Stop ALL polling when the chat provider unmounts (e.g. navigating away).
  useEffect(() => {
    return () => chatInstance.unsubscribeFromMessages();
  }, [chatInstance]);

  useEffect(() => {
    if (currentConversation && !currentConversation.isNew) {
      markAsRead();
    }
  }, [currentConversation, markAsRead]);

  // Keep the current user id on the singleton at all times (not just on first
  // init) so message.isOwned and other-participant resolution are always correct,
  // even across remounts where the singleton persists but state was reset.
  useEffect(() => {
    if (data?.getCurrentUser?.user?.uid) {
      ChatManager.myUserId = data.getCurrentUser.user.uid;
    }
  }, [data]);

  useEffect(() => {
    if (data?.getCurrentUser?.user && !chatInstance.initialized) {
      setLoadingCoversations(true);
      ChatManager.login()
        .then(() =>
          chatInstance.connect({
            onConversationUpdate: handleConversationUpdate,
            onConversationAdd: handleConversationUpdate,
            conversationLimit: CONVERSATION_LIMIT,
          }),
        )
        .then((conversations) => {
          setConversations(serializeConversations(conversations));
          setHasMoreConversations(chatInstance.hasMoreConversations);
        })
        .finally(() => setLoadingCoversations(false));
    }

    if (!data?.getCurrentUser?.user && chatInstance.initialized) {
      logout();
    }
  }, [data, chatInstance, handleConversationUpdate, logout]);

  const fetchMoreConversations = useCallback(() => {
    if (!hasMoreConversations || fetchingMoreConversations) return;
    setFetchingMoreConversations(true);
    chatInstance
      .fetchMoreConversations()
      .then((all) => {
        setConversations(serializeConversations(all));
        setHasMoreConversations(chatInstance.hasMoreConversations);
      })
      .finally(() => setFetchingMoreConversations(false));
  }, [chatInstance, hasMoreConversations, fetchingMoreConversations]);

  useEffect(() => {
    updateTotalUnread();
  }, [updateTotalUnread]);

  const contextValue = useMemo(
    () => ({
      conversations,
      currentConversation,
      currentMessages,
      toggleCurrentConversation,
      sendTextMessage,
      markAsRead,
      sendMediaMessage,
      fetchMoreMessages,
      hasMoreMessages,
      loadingConversations,
      loadingMessages,
      sendingMessage,
      fetchingMoreMessages,
      setCurrentConversation,
      totalUnreadCount,
      fetchMoreConversations,
      hasMoreConversations,
      fetchingMoreConversations,
      uploadingMedia,
    }),
    [
      conversations,
      toggleCurrentConversation,
      currentConversation,
      currentMessages,
      sendTextMessage,
      markAsRead,
      sendMediaMessage,
      fetchMoreMessages,
      hasMoreMessages,
      loadingConversations,
      loadingMessages,
      sendingMessage,
      fetchingMoreMessages,
      setCurrentConversation,
      totalUnreadCount,
      fetchMoreConversations,
      hasMoreConversations,
      fetchingMoreConversations,
      uploadingMedia,
    ],
  );

  return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>;
};
